import type { TaxonomyClass, VerificationScopeType } from "@sitescene/types";

import { useState } from "react";

import { Button } from "@/components/ui/buttons/button";
import { Input } from "@/components/ui/input/input";
import { NativeSelect } from "@/components/ui/select/select-native";
import { type PriorWriteInput } from "@/features/plausibility/use-priors";

import {
	draftLogOdds,
	draftToWriteInput,
	EMPTY_DRAFT,
	HOUR_BUCKETS,
	isDraftValid,
	type PriorDraft,
	SEASONS,
} from "./prior-draft";

const SCOPES: Exclude<VerificationScopeType, "global">[] = [
	"installation",
	"zone",
	"sensor",
];

export interface PriorFormProps {
	busy: boolean;
	classes: TaxonomyClass[];
	error: Error | null;
	onSubmit: (
		input: PriorWriteInput & {
			scopeRef: string;
			scopeType: Exclude<VerificationScopeType, "global">;
		},
	) => void;
	scoped?: boolean;
}

/** Adds a prior. Existing rows are edited in the table, not here. */
export const PriorForm = ({
	busy,
	classes,
	error,
	onSubmit,
	scoped = false,
}: PriorFormProps) => {
	const [draft, setDraft] = useState<PriorDraft>(EMPTY_DRAFT);
	const [scopeType, setScopeType] =
		useState<Exclude<VerificationScopeType, "global">>("installation");
	const [scopeRef, setScopeRef] = useState("");
	const patch = (changes: Partial<PriorDraft>) =>
		setDraft((current) => ({ ...current, ...changes }));

	const valid = isDraftValid(draft, classes) && (!scoped || scopeRef.trim());
	const logPriorOdds = draftLogOdds(draft);

	return (
		<form
			className="flex flex-wrap items-end gap-3"
			onSubmit={(event) => {
				event.preventDefault();
				if (!valid) return;
				onSubmit({
					...draftToWriteInput(draft),
					scopeRef: scopeRef.trim(),
					scopeType,
				});
			}}
		>
			{scoped && (
				<>
					<NativeSelect
						label="Scope"
						onChange={(e) => setScopeType(e.target.value as typeof scopeType)}
						options={SCOPES.map((s) => ({ label: s, value: s }))}
						value={scopeType}
					/>
					<Input
						className="w-auto"
						label="Ref (Mongo id)"
						onChange={setScopeRef}
						value={scopeRef}
					/>
				</>
			)}
			<NativeSelect
				label="Class"
				onChange={(e) => patch({ classId: e.target.value })}
				options={[
					{ label: "Choose…", value: "" },
					...classes.map((cls) => ({
						label: cls.classId,
						value: cls.classId,
					})),
				]}
				value={draft.classId}
			/>
			<NativeSelect
				label="Season"
				onChange={(e) =>
					patch({ season: e.target.value as PriorDraft["season"] })
				}
				options={[
					{ label: "any", value: "" },
					...SEASONS.map((s) => ({ label: s, value: s })),
				]}
				value={draft.season}
			/>
			<NativeSelect
				label="Hours"
				onChange={(e) =>
					patch({ hourBucket: e.target.value as PriorDraft["hourBucket"] })
				}
				options={[
					{ label: "any", value: "" },
					...HOUR_BUCKETS.map((h) => ({ label: h, value: h })),
				]}
				value={draft.hourBucket}
			/>
			<Input
				className="w-auto"
				inputMode="decimal"
				label="Prior probability (%)"
				onChange={(percent) => patch({ percent })}
				value={draft.percent}
			/>
			<span className="pb-1 text-xs text-tertiary">
				= log odds {Number.isNaN(logPriorOdds) ? "—" : logPriorOdds.toFixed(2)}
			</span>
			<Input
				className="w-auto"
				label="Notes"
				onChange={(notes) => patch({ notes })}
				value={draft.notes}
			/>
			<Button isDisabled={busy || !valid} size="sm" type="submit">
				Save prior
			</Button>
			{error && (
				<span className="text-sm text-error-primary" role="alert">
					{error.message}
				</span>
			)}
		</form>
	);
};
